import {
  ContentCopy as ContentCopyIcon,
  Inventory as InventoryIcon,
} from "@mui/icons-material";
import clsx from "clsx";
import { FC, MouseEventHandler, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import { AccessControl, MuiButton } from "@ryde/controls";
import { useModalConfirm } from "@ryde/redux";
import { ConfirmResult, RydeUiUnit } from "@ryde/shared";

import {
  EditFormFooterButton,
  LayoutRightFooterButtonsArea,
  LayoutRightFooterFormWidthArea,
  LayoutRightFooterStyled,
} from "./layout.right.styled";

interface ILayoutRightFooterProps {
  loading?: boolean;
  deleteMessage?: string;
  duplicationMessage?: string;
  closeClick?: MouseEventHandler<HTMLElement>;
  cancelClick?: MouseEventHandler<HTMLElement>;
  saveClick?: MouseEventHandler<HTMLElement>;
  isSaveDisabled?: boolean;
  isInnerItemEditing?: boolean;
  archiveClick?: MouseEventHandler<HTMLElement>;
  isArchiveDisabled?: boolean;
  duplicateClick?: MouseEventHandler<HTMLElement>;
  isDuplicateDisabled?: boolean;
  entity?: RydeUiUnit;
  saveLabel?: string;
  cancelLabel?: string;
  customButtons?: ReactNode;
}

export const LayoutRightFooter: FC<ILayoutRightFooterProps> = ({
  saveClick,
  closeClick,
  cancelClick,
  isSaveDisabled = false,
  isInnerItemEditing = false,
  archiveClick,
  isArchiveDisabled = false,
  duplicateClick,
  isDuplicateDisabled = false,
  loading,
  deleteMessage,
  duplicationMessage,
  entity = "unknown",
  saveLabel,
  cancelLabel,
  customButtons,
}) => {
  const { t } = useTranslation();
  const { confirmDialogResult } = useModalConfirm();

  const archiveMessage = deleteMessage || t(`general.ITEM_ARCHIVE_MESSAGE`);
  const dupMessage = duplicationMessage || t(`general.ITEM_DUPLICATE_MESSAGE`);

  const onArchiveClickEvent = useEvent(
    async (e: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
      if (archiveClick) {
        const confirmationResult = await confirmDialogResult({
          title: t("general.ARCHIVE_CONFIRM"),
          message: archiveMessage,
        });

        if (confirmationResult === ConfirmResult.OK) {
          archiveClick(e);
        }
      }
    },
  );

  const onDuplicateClickEvent = useEvent(
    async (e: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
      if (duplicateClick) {
        const confirmationResult = await confirmDialogResult({
          title: t("general.DUPLICATE_CONFIRM"),
          message: dupMessage,
        });

        if (confirmationResult === ConfirmResult.OK) {
          duplicateClick(e);
        }
      }
    },
  );
  return (
    <LayoutRightFooterStyled
      data-e2e-entity={entity}
      className={clsx(isInnerItemEditing && "inner-item-editing")}
    >
      <LayoutRightFooterFormWidthArea>
        {archiveClick && (
          <AccessControl entity={entity || "unknown"} action="delete">
            <EditFormFooterButton
              onClick={onArchiveClickEvent}
              disabled={isArchiveDisabled || loading}
            >
              <InventoryIcon style={{ fontSize: "16px" }} />
              <span>{t(`general.ARCHIVE`)}</span>
            </EditFormFooterButton>
          </AccessControl>
        )}
        {duplicateClick && (
          <AccessControl entity={entity || "unknown"} action="create">
            <EditFormFooterButton
              onClick={onDuplicateClickEvent}
              disabled={isDuplicateDisabled || loading}
            >
              <ContentCopyIcon style={{ fontSize: "16px" }} />
              <span>{t(`general.DUPLICATE`)}</span>
            </EditFormFooterButton>
          </AccessControl>
        )}
        <LayoutRightFooterButtonsArea>
          <MuiButton
            e2eData={`${entity}_cancel_button_right_footer`}
            muiVariant={"outlined"}
            color={"secondary"}
            onClick={cancelClick || closeClick}
            loading={loading}
          >
            {cancelLabel || t(`general.CANCEL`)}
          </MuiButton>

          {saveClick && (
            <MuiButton
              e2eData={`${entity}_save_button_right_footer`}
              loading={loading}
              muiStyle={isSaveDisabled ? "error" : "primary"}
              disabled={isSaveDisabled}
              onClick={saveClick}
              sx={
                isInnerItemEditing
                  ? {
                      border: "2px dashed",
                      borderColor: "warning.main",
                    }
                  : undefined
              }
            >
              {saveLabel || t(`general.SAVE`)}
            </MuiButton>
          )}
          {customButtons}
        </LayoutRightFooterButtonsArea>
      </LayoutRightFooterFormWidthArea>
    </LayoutRightFooterStyled>
  );
};
